import React, { useState, useEffect } from 'react';
import { Snackbar, Button, Box, Typography, LinearProgress } from '@mui/material';
import { SystemUpdateAlt, RestartAlt } from '@mui/icons-material';
import useOtaUpdate from '../hooks/useOtaUpdate';

/**
 * UpdateBanner — Shows a non-intrusive banner for:
 * 1. Native OTA updates with download progress (X / Y MB) and restart button.
 * 2. Service Worker web updates.
 */
const UpdateBanner = () => {
    const [showSwUpdate, setShowSwUpdate] = useState(false);
    const { isDownloading, downloadProgress, isReadyToRestart, reloadApp } = useOtaUpdate();

    useEffect(() => {
        let intervalId = null;
        let registration = null;

        const checkForUpdate = async () => {
            if (registration) {
                try {
                    await registration.update();
                } catch (err) {
                    // Silent fail
                }
            }
        };

        const startPolling = () => {
            if (intervalId) clearInterval(intervalId);
            const interval = document.hidden ? 5 * 60 * 1000 : 60 * 1000;
            intervalId = setInterval(checkForUpdate, interval);
        };

        const handleVisibilityChange = () => {
            startPolling();
            if (!document.hidden) {
                checkForUpdate();
            }
        };

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistration().then((reg) => {
                if (!reg) return;
                registration = reg;

                if (reg.waiting) {
                    setShowSwUpdate(true);
                    return;
                }

                reg.addEventListener('updatefound', () => {
                    const newWorker = reg.installing;
                    if (!newWorker) return;

                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            console.log('[UpdateBanner] New service worker installed');
                            setShowSwUpdate(true);
                        }
                    });
                });

                startPolling();
            });
        }

        const handlePushUpdate = () => {
            checkForUpdate();
        };

        window.addEventListener('app-update-available', () => setShowSwUpdate(true));
        window.addEventListener('check-for-update', handlePushUpdate);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (intervalId) clearInterval(intervalId);
            window.removeEventListener('app-update-available', () => setShowSwUpdate(true));
            window.removeEventListener('check-for-update', handlePushUpdate);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const handleWebUpdate = () => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistration().then((reg) => {
                if (reg?.waiting) {
                    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
                }
            });
        }
        setTimeout(() => {
            window.location.reload();
        }, 300);
    };

    const isOpen = isDownloading || isReadyToRestart || showSwUpdate;

    if (!isOpen) return null;

    // Format MB and percentage
    let progressPercent = 0;
    let progressMbText = '';
    if (downloadProgress) {
        progressPercent = Math.min(Math.max(downloadProgress.percent || 0, 0), 100);
        if (downloadProgress.bytes && downloadProgress.total) {
            const downloadedMb = (downloadProgress.bytes / (1024 * 1024)).toFixed(1);
            const totalMb = (downloadProgress.total / (1024 * 1024)).toFixed(1);
            progressMbText = `${downloadedMb} / ${totalMb} MB (${Math.round(progressPercent)}%)`;
        } else if (progressPercent > 0) {
            progressMbText = `${Math.round(progressPercent)}%`;
        }
    }

    return (
        <Snackbar
            open={isOpen}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ bottom: { xs: 80, sm: 24 }, zIndex: 9999 }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 2,
                py: 1.5,
                borderRadius: 2,
                boxShadow: 6,
                minWidth: 280,
                maxWidth: 380,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {isReadyToRestart ? (
                        <RestartAlt sx={{ fontSize: 22 }} />
                    ) : (
                        <SystemUpdateAlt sx={{ fontSize: 22 }} />
                    )}

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {isDownloading
                                ? 'Laster ned oppdatering...'
                                : isReadyToRestart
                                    ? 'Oppdatering klar!'
                                    : 'Ny versjon tilgjengelig'}
                        </Typography>
                        {isDownloading && (
                            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem', display: 'block' }}>
                                {progressMbText || 'Forbereder nedlasting...'}
                            </Typography>
                        )}
                        {isReadyToRestart && (
                            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem', display: 'block' }}>
                                Start appen på nytt for å gjelde
                            </Typography>
                        )}
                    </Box>

                    {isReadyToRestart ? (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={reloadApp}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.25)',
                                color: 'inherit',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' },
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Omstart
                        </Button>
                    ) : showSwUpdate && !isDownloading ? (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleWebUpdate}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.25)',
                                color: 'inherit',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' },
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Oppdater
                        </Button>
                    ) : null}
                </Box>

                {isDownloading && (
                    <LinearProgress
                        variant={progressPercent > 0 ? "determinate" : "indeterminate"}
                        value={progressPercent}
                        sx={{
                            borderRadius: 1,
                            height: 4,
                            bgcolor: 'rgba(255,255,255,0.3)',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: 'white'
                            }
                        }}
                    />
                )}
            </Box>
        </Snackbar>
    );
};

export default UpdateBanner;
